import apiClient from '@shared/api/apiClient';

/**
 * 문서 저장/상신 API
 * - PC 버전과 동일한 엔드포인트 사용 (/pc/approval/doc)
 * - mode: 0=새문서 상신, 1=임시문서 상신, 2=재상신문서 상신, 3=임시저장
 * @param {Object} documentData - 문서 데이터
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function saveDocument(documentData) {
  // 문서 내용을 Base64로 인코딩 (PC 버전과 동일)
  const contentsBase64 = documentData.contents
    ? btoa(unescape(encodeURIComponent(documentData.contents)))
    : '';

  const requestData = {
    // 문서 식별자 (PUT 수정/재상신 시 필수)
    docIdx: documentData.docIdx,
    mode: documentData.mode,
    company: documentData.company,
    enumber: documentData.enumber,
    docType: documentData.docType,
    title: documentData.title,
    contents: contentsBase64,
    security: documentData.security || 3,
    hurry: documentData.hurry || 0,
    docState: documentData.docState || 2,
    // 결재자 정보
    sign1: documentData.sign1 || 0,
    sign2: documentData.sign2 || 0,
    sign3: documentData.sign3 || 0,
    sign4: documentData.sign4 || 0,
    sign5: documentData.sign5 || 0,
    // 검토자 정보
    check1: documentData.check1 || 0,
    check2: documentData.check2 || 0,
    check3: documentData.check3 || 0,
    check4: documentData.check4 || 0,
    // 합의자 정보
    agreement: documentData.agreement || 0,
    // 결재 순서
    sign1_order: documentData.sign1_order || 0,
    sign2_order: documentData.sign2_order || 0,
    sign3_order: documentData.sign3_order || 0,
    sign4_order: documentData.sign4_order || 0,
    sign5_order: documentData.sign5_order || 0,
    check1_order: documentData.check1_order || 0,
    check2_order: documentData.check2_order || 0,
    check3_order: documentData.check3_order || 0,
    check4_order: documentData.check4_order || 0,
    agreement_order: documentData.agreement_order || 0,
    // 결재 상태 (PC 버전과 동일)
    sign1_state: documentData.sign1_state || 3,
    sign2_state: documentData.sign2_state || 1,
    sign3_state: documentData.sign3_state || 1,
    sign4_state: documentData.sign4_state || 1,
    sign5_state: documentData.sign5_state || 1,
    check1_state: documentData.check1_state || 1,
    check2_state: documentData.check2_state || 1,
    check3_state: documentData.check3_state || 1,
    check4_state: documentData.check4_state || 1,
    agreement_state: documentData.agreement_state || 1,
    // 수신자/시행자/참조문서
    senderList: documentData.senderList || [],
    executorList: documentData.executorList || [],
    referenceDocList: documentData.referenceDocList || [],
    // 첨부파일 목록 (기존 파일 idx 배열)
    file_list: documentData.file_list || [],
    // 요약 및 비고
    summaryNote: documentData.summaryNote || '',
    // 법인인감 사용 여부
    is_corp_seal_used:
      documentData.is_corp_seal_used !== undefined ? documentData.is_corp_seal_used : 0,
  };

  // 문서 본문 이미지가 있으면 추가 (Base64 인코딩된 PNG 이미지)
  if (documentData.contents_img) {
    requestData.contents_img = documentData.contents_img;
  }

  // 백엔드(DocumentController) 기준:
  // - POST /api/v1/documents : mode(0/2/3) 처리(새문서상신/재상신/임시저장)
  // - PUT  /api/v1/documents : 문서 수정(재상신/수정) - docIdx 필수, mode 2만 처리
  // 프론트 정책:
  // - 양식선택에서 최초 작성(신규 문서): docIdx 없음 → POST mode 0
  // - 임시저장 문서 상신: docIdx 있어도 POST mode 0 (상신 성공 후 프론트에서 임시저장 문서 삭제)
  // - 수정요청 문서 수정(기존 문서 처리, mode 2): docIdx 있음 → PUT mode 2
  const hasDocIdx = typeof requestData.docIdx === 'number' && requestData.docIdx > 0;
  const method = hasDocIdx && requestData.mode === 2 ? 'put' : 'post';

  const response = await apiClient[method]('/v1/documents', requestData, {
    withAuth: true,
  });

  return response.data;
}

/**
 * 문서 첨부파일 업로드 API
 * POST /api/v1/documents/{docIdx}/files/upload
 * (PcController에서 DocumentController로 마이그레이션)
 * - MultipartFile 형식으로 파일 전송
 * - 타임아웃은 파일 크기에 따라 동적으로 계산 (최소 30초, 1MB당 1초 추가)
 * @param {number} docIdx - 문서 인덱스
 * @param {File} file - 업로드할 파일
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function uploadDocumentFile(docIdx, file) {
  const formData = new FormData();
  formData.append('file', file);

  // 파일 크기에 따른 타임아웃 계산 (최소 30초, 1MB당 1초 추가, 최대 5분)
  // 예: 100MB 파일 = 30초 + 100초 = 130초
  const fileSizeMB = file.size / (1024 * 1024);
  const calculatedTimeout = Math.min(30000 + fileSizeMB * 1000, 300000); // 최대 5분

  const response = await apiClient.post(`/v1/documents/${docIdx}/files/upload`, formData, {
    withAuth: true,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: calculatedTimeout,
  });

  return response.data;
}

/**
 * 사용자 서명 조회 API
 * - 상신 전 서명 존재 여부 확인용
 * @returns {Promise<Object>} 서명 이미지 데이터
 */
async function checkMySign() {
  const response = await apiClient.get('/v1/users/me/signature', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 양식별 기본 수신자 목록 조회 API
 * @param {number} docType - 문서 양식 코드
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 수신자 목록
 */
async function getFormReceiverList(docType, companyCode) {
  const response = await apiClient.post(
    '/v1/forms/receivers',
    {
      doc: docType,
      company: companyCode,
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 양식별 기본 시행자 목록 조회 API
 * @param {number} docType - 문서 양식 코드
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 시행자 목록
 */
async function getFormExecutorList(docType, companyCode) {
  const response = await apiClient.post(
    '/v1/forms/executors',
    {
      doc: docType,
      company: companyCode,
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 임시저장 문서 목록 조회 API
 * POST /api/v1/documents/list/tempDoc
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {Object} params - 검색 조건
 * @returns {Promise<Object>} 임시저장 문서 목록
 */
async function getTemporaryDocList(params = {}) {
  const response = await apiClient.post('/v1/documents/list/tempDoc', params, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 임시저장 문서 상세 조회 API
 * GET /api/v1/documents/{docIdx}
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 문서 상세 정보
 */
async function getTemporaryDocDetail(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 임시저장 문서 삭제 API
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 삭제 결과
 */
async function deleteTemporaryDoc(docIdx) {
  const response = await apiClient.delete(`/v1/approvals/doc/${docIdx}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 수신자 목록 업데이트 API
 * PUT /api/v1/documents/sender-list
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @param {number} companyCode - 회사 코드
 * @param {Array} senderList - 수신자 목록
 * @returns {Promise<Object>} 업데이트 결과
 */
async function updateReceiverList(docIdx, companyCode, senderList) {
  const response = await apiClient.put(
    '/v1/documents/sender-list',
    {
      docIdx,
      company: companyCode,
      senderList: senderList || [],
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 문서 시행자 목록 업데이트 API
 * PUT /api/v1/documents/executor
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @param {Array} enumberList - 시행자 사원번호 목록
 * @returns {Promise<Object>} 업데이트 결과
 */
async function updateExecutorList(docIdx, enumberList) {
  const response = await apiClient.put(
    '/v1/documents/executor',
    {
      docIdx,
      enumberList: enumberList || [],
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 문서 첨부파일 다운로드 URL 조회 API
 * (Webview 등에서 Blob 에러 방지를 위해 실제 물리 파일 경로를 반환)
 * @param {number} fileIdx - 파일 인덱스
 * @returns {Promise<Object>} 파일 정보 (fileName, fileSize, fileURL)
 */
async function downloadDocumentFile(fileIdx) {
  const response = await apiClient.get(`/v1/documents/files/${fileIdx}/download-url`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 참조 가능 문서 목록 조회 API (참조문서 선택용)
 * @param {Object} params - 검색 조건
 * @returns {Promise<Object>} 참조 가능 문서 목록
 */
async function getReferenceDocList(params = {}) {
  const response = await apiClient.post('/v1/documents/search/references', params, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 수신자 목록 조회 API
 * GET /api/v1/documents/{docIdx}/receivers
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 수신자 목록
 */
async function getDocumentReceiverList(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/receivers`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 시행자 목록 조회 API
 * GET /api/v1/documents/{docIdx}/executors
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 시행자 목록
 */
async function getDocumentExecutorList(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/executors`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 참조문서 목록 조회 API
 * GET /api/v1/documents/{docIdx}/references
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 참조문서 목록
 */
async function getDocumentReferenceDocList(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/references`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 첨부파일 목록 조회 API
 * GET /api/v1/documents/{docIdx}/files
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 첨부파일 목록
 */
async function getDocumentFileList(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/files`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 코멘트 목록 조회 API
 * GET /api/v1/documents/{docIdx}/comments
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 코멘트 목록
 */
async function getDocumentCommentList(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/comments`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 코멘트 추가 API
 * @param {number} docIdx - 문서 인덱스
 * @param {string} comment - 코멘트 내용
 * @returns {Promise<Object>} API 응답
 */
async function addDocumentComment(docIdx, comment) {
  const response = await apiClient.put(
    '/v1/documents/comments',
    { doc_idx: docIdx, comment },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 문서 코멘트 수정 API
 * @param {number} idx - 코멘트 인덱스
 * @param {number} docIdx - 문서 인덱스
 * @param {string} comment - 수정할 코멘트 내용
 * @returns {Promise<Object>} API 응답
 */
async function modifyDocumentComment(idx, docIdx, comment) {
  const response = await apiClient.put(
    '/v1/documents/comments/modify',
    { idx, doc_idx: docIdx, comment },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 문서 코멘트 삭제 API
 * @param {number} idx - 코멘트 인덱스
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} API 응답
 */
async function deleteDocumentComment(idx, docIdx) {
  const response = await apiClient.put(
    '/v1/documents/comments/delete',
    { idx, doc_idx: docIdx },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 문서 본문 이미지 조회 API
 * GET /api/v1/documents/{docIdx}/content-image
 * - 모바일 화면에서 문서 본문을 이미지로 표시할 때 사용
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} 이미지 정보 (fileName, fileSize, fileURL)
 */
async function getDocumentContentImage(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/content-image`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 본문 이미지만 갱신 API (단일 테스트 / 재캡처 저장용)
 * PUT /api/v1/documents/{docIdx}/content-image
 * @param {number} docIdx - 문서 인덱스
 * @param {string} contentsImgBase64 - Base64 인코딩된 PNG 문자열
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function updateDocumentContentImage(docIdx, contentsImgBase64) {
  const response = await apiClient.put(
    `/v1/documents/${docIdx}/content-image`,
    { contents_img: contentsImgBase64 },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 재캡처 전용: 회사 구분 없이 문서 본문(contents, contents_html)만 조회
 * GET /api/v1/documents/{docIdx}/content-for-recapture
 * 다른 회사 문서도 본문만 가져와 재캡처·저장 가능.
 * @param {number} docIdx - 문서 인덱스
 * @returns {Promise<Object>} { result_code, resultData: { contents, contents_html } }
 */
async function getDocumentContentForRecapture(docIdx) {
  const response = await apiClient.get(`/v1/documents/${docIdx}/content-for-recapture`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 검색 필터 조회 API
 * GET /api/v1/documents/search/filters
 * (PcController에서 DocumentController로 마이그레이션)
 * @returns {Promise<Object>} 필터 목록 (teamNameList, docType)
 */
async function getDocumentSearchFilters() {
  const response = await apiClient.get('/v1/documents/search/filters', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 삭제 처리 (상태 변경 state=5)
 * PUT /api/v1/documents/{idx}/remove
 * @param {number} idx - 문서 인덱스
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function removeDocument(idx) {
  const response = await apiClient.put(
    `/v1/documents/${idx}/remove`,
    {},
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 문서 기안일자 수정 API (어드민 전용)
 * PUT /api/v1/approvals/doc-date
 * @param {Object} data - { idx, date, enumber }
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function updateApprovalDocDate(data) {
  const response = await apiClient.put('/v1/approvals/doc-date', data, {
    withAuth: true,
  });
  return response.data;
}

export {
  saveDocument,
  uploadDocumentFile,
  checkMySign,
  getFormReceiverList,
  getFormExecutorList,
  getTemporaryDocList,
  getTemporaryDocDetail,
  getDocumentContentForRecapture,
  deleteTemporaryDoc,
  updateReceiverList,
  updateExecutorList,
  downloadDocumentFile,
  getReferenceDocList,
  getDocumentReceiverList,
  getDocumentExecutorList,
  getDocumentReferenceDocList,
  getDocumentFileList,
  getDocumentCommentList,
  addDocumentComment,
  modifyDocumentComment,
  deleteDocumentComment,
  getDocumentContentImage,
  getDocumentSearchFilters,
  removeDocument,
  updateApprovalDocDate,
  updateDocumentContentImage,
};
